import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';
import { Tag, useDelayedFlag } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import { TimeOffStatus, TimeOffType } from '../../store/slices/timeOffSlice';
import { toApiDate } from '../../components/modals/time-off/timeOffHours';
import { formatNumber, MISSING } from '../../utils/format';

/** Form state for the "Schedule time off" dialog (owned by HRTimeOffManager). */
export interface TimeOffFormData {
  employeeId: string;
  startDate: Date | null;
  endDate: Date | null;
  type: TimeOffType;
  hours: number;
  notes: string;
}

/** Group key for employees without a department in the "Log time off" table. */
export const UNASSIGNED_DEPARTMENT_ID = '__unassigned__';

/** The one name for a missing department (or team) across this page. */
export const NO_DEPARTMENT_LABEL = 'No department';

const TYPE_LABELS: Record<string, string> = {
  [TimeOffType.PTO]: 'PTO',
  [TimeOffType.SICK]: 'Sick',
  [TimeOffType.WFH]: 'WFH',
};

const TYPE_PHRASES: Record<string, string> = {
  [TimeOffType.PTO]: 'PTO',
  [TimeOffType.SICK]: 'sick time',
  [TimeOffType.WFH]: 'WFH',
};

/** "PTO" · "Sick" · "WFH" — the label for a time-off type. */
export const typeLabel = (type: string) => TYPE_LABELS[type] ?? type;

/** The type as it reads inside a sentence: "Logged sick time for Maria Chen". */
export const typePhrase = (type: string) => TYPE_PHRASES[type] ?? type;

/** Time-off type as a category tag (types are labels, never status). */
export const TypeTag: React.FC<{ type: string }> = ({ type }) => <Tag>{typeLabel(type)}</Tag>;

/** PTO that starts sooner than this many days out always waits for approval. */
const PTO_NOTICE_DAYS = 14;

/**
 * Says before saving when the request will land in the approval queue, by the
 * rules the API applies to new time off (decideCreateStatus in
 * backend/src/utils/timeOffRequestPolicy.ts). PTO starting within two weeks
 * always waits. Other PTO waits unless an admin books it. WFH waits on VP
 * review for anyone not set up for it, and sick time is approved on arrival.
 * The toast after saving reads the status the API actually stored.
 */
export const queueNoteFor = (
  type: string,
  startDate: Date | null,
  { isAdmin, wfhEligible }: { isAdmin: boolean; wfhEligible?: boolean },
  today: Date = new Date(),
): string | null => {
  if (type === TimeOffType.PTO) {
    if (startDate && isValid(startDate) && differenceInCalendarDays(startDate, today) < PTO_NOTICE_DAYS) {
      return 'PTO with less than two weeks’ notice goes to the approval queue when you save it.';
    }
    return isAdmin ? null : 'This PTO goes to the approval queue when you save it.';
  }
  if (type === TimeOffType.WFH && !wfhEligible) {
    return 'WFH for in-office staff goes to VP review when you save it.';
  }
  return null;
};

/**
 * POST /time-off body for the schedule dialog, once its range has validated.
 * The dates go as calendar days, as the employee form and quick log send
 * them: the API checks the hours against the days it stores, and the
 * dialog's dates keep the time they were picked, so a UTC timestamp from the
 * evening is already the next day.
 */
export const buildScheduleBody = (form: TimeOffFormData, status: TimeOffStatus) => ({
  employee: form.employeeId,
  startDate: toApiDate(form.startDate as Date),
  endDate: toApiDate(form.endDate as Date),
  type: form.type,
  hours: form.hours,
  notes: form.notes,
  status,
});

/** 8 → "8h" · 4.5 → "4.5h" · −8 → "−8h" (true minus) · missing → "—". */
export const formatHours = (hours: number | null | undefined) => {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) return MISSING;
  const digits = Number.isInteger(hours) ? 0 : 1;
  return `${hours < 0 ? '−' : ''}${formatNumber(Math.abs(hours), digits)}h`;
};

/** "Maria Chen’s" */
export const possessive = (name: string) => `${name}’s`;

/** Inclusive calendar days in an entry (Oct 12–14 = 3). */
export const entryDays = (startDate: string, endDate: string) =>
  differenceInCalendarDays(parseISO(endDate), parseISO(startDate)) + 1;

/** "Starts tomorrow" · "Starts in 4 days" · "Started 2 days ago" — only when it is close. */
export const startsLabel = (startDate: string, today: Date) => {
  const diff = differenceInCalendarDays(parseISO(startDate), today);
  if (Number.isNaN(diff)) return null;
  if (diff === 0) return 'Starts today';
  if (diff === 1) return 'Starts tomorrow';
  if (diff > 1 && diff <= 14) return `Starts in ${diff} days`;
  if (diff === -1) return 'Started yesterday';
  if (diff < -1) return `Started ${Math.abs(diff)} days ago`;
  return null;
};

/**
 * Row actions stay quiet until the row is hovered or something inside it has
 * keyboard focus; on touch screens (no hover) they are always visible.
 * Only for rows whose actions are also reachable another way (the approvals
 * queue opens a sheet with the same decisions). Never hide the only path.
 */
export const revealRowActions = {
  '& .row-actions': { opacity: 0, transition: `opacity ${sbg.duration.fast} ${sbg.ease.standard}` },
  '&:hover .row-actions, &:focus-within .row-actions': { opacity: 1 },
  '@media (hover: none)': { '& .row-actions': { opacity: 1 } },
} as const;

/** Inline cell text for "nothing here" in tables. */
export const MissingValue: React.FC = () => (
  <Box component="span" sx={{ color: sbg.text.tertiary }}>
    {MISSING}
  </Box>
);

/** True when a keyboard shortcut should be ignored (typing in a field, or a modifier is held). */
export const isTypingTarget = (event: KeyboardEvent) => {
  if (event.metaKey || event.ctrlKey || event.altKey) return true;
  const target = event.target as HTMLElement | null;
  return Boolean(target?.closest?.('input, textarea, select, [contenteditable="true"], [role="combobox"]'));
};

/** Press "/" anywhere on the page to jump to a search field. */
export const useSlashToFocus = (ref: React.RefObject<HTMLInputElement | null>, enabled = true) => {
  React.useEffect(() => {
    if (!enabled) return undefined;
    const handler = (event: KeyboardEvent) => {
      if (event.key !== '/' || isTypingTarget(event)) return;
      // Leave "/" alone while a dialog, sheet or menu is open.
      if (document.querySelector('.MuiModal-root:not(.MuiModal-hidden)')) return;
      event.preventDefault();
      ref.current?.focus();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [ref, enabled]);
};

/** A content-sized skeleton block revealed after 300ms, for regions that are not tables. */
export const RegionSkeleton: React.FC<{ label: string; height: number | string }> = ({ label, height }) => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Box role="status" aria-label={label} aria-busy="true">
      <span className="sbg-sr-only">{label}</span>
      <Skeleton
        variant="rounded"
        height={height}
        aria-hidden="true"
        sx={{ borderRadius: sbg.radius.card, visibility: visible ? 'visible' : 'hidden' }}
      />
    </Box>
  );
};
